/**
 * COLP/COFA Vouching for ID Section
 * Allows authorized directors to vouch for client identity
 * when standard Thirdfort/AML ID verification is not possible.
 * Supports backdating the approval date.
 */
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Shield, UserCheck, FileText, Calendar, AlertTriangle, CheckCircle2,
  ChevronDown, ChevronUp, X, Loader2, Ban, Download
} from "lucide-react";
import { toast } from "sonner";

import { API } from '../config/api';

const EMPTY_FORM = {
  client_name: "",
  matter_reference: "",
  provisional_id: "",
  reason: "",
  approval_date: new Date().toISOString().split("T")[0],
  client_type: "",
  id_documents_seen: "",
  relationship_to_client: "",
  notes: "",
};

export default function VouchingForID({ directorEmail: propEmail, directorName: propName }) {
  // Trust the prop from parent — parent (DirectorAnalytics) resolves identity robustly
  const directorEmail = propEmail || "";
  const [directors, setDirectors] = useState({});
  useEffect(() => {
    fetch(`${API}/staff`).then(r => r.json()).then(d => setDirectors(d.directors || {})).catch(() => {});
  }, []);
  const directorName = propName || directors[directorEmail] || directorEmail;
  const [expanded, setExpanded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);
  const [vouches, setVouches] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [revoking, setRevoking] = useState(null);
  const [revokeReason, setRevokeReason] = useState("");

  const fetchData = useCallback(async () => {
    if (!directorEmail) return;
    setLoading(true);
    try {
      const [vRes, sRes] = await Promise.all([
        fetch(`${API}/vouching/list?director_email=${directorEmail}&status=${filter}&limit=50`),
        fetch(`${API}/vouching/stats?director_email=${directorEmail}`),
      ]);
      if (vRes.ok) {
        const vData = await vRes.json();
        setVouches(vData.vouches || []);
      }
      if (sRes.ok) setStats(await sRes.json());
    } catch (e) {
      console.error("Failed to fetch vouching data:", e);
      toast.error("Failed to load vouching data");
    }
    setLoading(false);
  }, [directorEmail, filter]);

  useEffect(() => { if (expanded) fetchData(); }, [expanded, fetchData]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.client_name.trim()) return toast.error("Client name is required");
    if (!form.reason.trim() || form.reason.trim().length < 10) return toast.error("Reason must be at least 10 characters");
    if (!form.approval_date) return toast.error("Approval date is required");

    setSubmitting(true);
    try {
      const resp = await fetch(`${API}/vouching/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          director_email: directorEmail,
          director_name: directorName,
        }),
      });
      const data = await resp.json();
      if (resp.ok && data.success) {
        toast.success(data.message);
        setForm({ ...EMPTY_FORM });
        setShowForm(false);
        fetchData();
      } else {
        toast.error(data.detail || "Failed to create vouching record");
      }
    } catch (e) {
      toast.error("Network error");
    }
    setSubmitting(false);
  }

  async function handleDownloadPDF(clientName) {
    try {
      const resp = await fetch(`${API}/vouching/pdf/${encodeURIComponent(clientName)}?director_email=${directorEmail}`);
      if (!resp.ok) { toast.error("Failed to generate PDF"); return; }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `VouchingForID_${clientName.replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Download failed");
    }
  }

  async function handleRevoke(clientName) {
    if (!revokeReason.trim()) return toast.error("Revocation reason required");
    setRevoking(clientName);
    try {
      const resp = await fetch(`${API}/vouching/revoke/${encodeURIComponent(clientName)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ director_email: directorEmail, revoke_reason: revokeReason }),
      });
      const data = await resp.json();
      if (resp.ok && data.success) {
        toast.success(data.message);
        setRevokeReason("");
        fetchData();
      } else {
        toast.error(data.detail || "Failed to revoke");
      }
    } catch (e) {
      toast.error("Network error");
    }
    setRevoking(null);
  }

  return (
    <Card className="bg-gray-900 border-gray-800 mb-6" data-testid="vouching-section">
      <CardHeader
        className="pb-2 pt-4 px-4 cursor-pointer select-none"
        onClick={() => setExpanded(!expanded)}
      >
        <CardTitle className="text-sm font-semibold text-gray-300 flex items-center justify-between">
          <span className="flex items-center gap-1.5">
            <UserCheck className="w-4 h-4 text-teal" />
            COLP/COFA — Vouching for ID
            {stats && stats.active > 0 && (
              <Badge className="bg-teal-dim/40 text-teal text-[10px] ml-2 border-teal/50">
                {stats.active} active
              </Badge>
            )}
          </span>
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
        </CardTitle>
      </CardHeader>

      {expanded && (
        <CardContent className="px-4 pb-4 space-y-4">
          {/* Auth guard */}
          {!directorEmail && (
            <div className="bg-status-amber-dim/20 border border-amber-700/40 rounded-lg p-3 text-xs text-status-amber flex items-center gap-2" data-testid="vouching-no-auth">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>Please log in with your director email (e.g. vanessa@bonsai.law) to access vouching. If you used the shared password, please log out and log in with your email and password instead.</span>
            </div>
          )}
          {directorEmail && <>
          {/* Stats row */}
          {stats && (
            <div className="grid grid-cols-4 gap-2" data-testid="vouching-stats">
              {[
                { label: "Active", value: stats.active, color: "text-teal" },
                { label: "Revoked", value: stats.revoked, color: "text-status-red" },
                { label: "Total", value: stats.total, color: "text-white" },
                { label: "Last 30d", value: stats.recent_30d, color: "text-blue-400" },
              ].map(s => (
                <div key={s.label} className="bg-gray-800/50 rounded p-2 text-center border border-gray-700/50">
                  <div className={`text-lg font-bold ${s.color}`}>{s.value}</div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider">{s.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* New vouch button */}
          {!showForm && (
            <Button
              size="sm"
              onClick={() => setShowForm(true)}
              className="bg-teal hover:bg-teal text-white"
              data-testid="new-vouch-btn"
            >
              <UserCheck className="w-3.5 h-3.5 mr-1" /> New ID Vouch
            </Button>
          )}

          {/* Form */}
          {showForm && (
            <form onSubmit={handleSubmit} className="bg-gray-800/60 rounded-lg p-4 border border-gray-700/60 space-y-3" data-testid="vouch-form">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-semibold text-teal flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5" /> Director Vouching for Client ID
                </h3>
                <button type="button" onClick={() => setShowForm(false)} className="text-gray-500 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <p className="text-[10px] text-status-amber/80 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                SRA requirement: Record must document why standard verification was not possible and what alternative steps were taken.
              </p>

              {/* Row 1: Client + Matter */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-gray-400 mb-1 block">Client Name *</label>
                  <Input
                    value={form.client_name}
                    onChange={e => set("client_name", e.target.value)}
                    placeholder="Full legal name"
                    className="bg-gray-900 border-gray-700 text-sm h-8"
                    data-testid="vouch-client-name"
                    required
                  />
                </div>
                <div>
                  <label className="text-[11px] text-gray-400 mb-1 block">Matter Reference</label>
                  <Input
                    value={form.matter_reference}
                    onChange={e => set("matter_reference", e.target.value)}
                    placeholder="BL.XX.001.XXXX"
                    className="bg-gray-900 border-gray-700 text-sm h-8"
                    data-testid="vouch-matter-ref"
                  />
                </div>
              </div>

              {/* Row 2: Matter Reference + Client Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-gray-400 mb-1 block">Matter Reference (OB/BL Ref)</label>
                  <Input
                    value={form.provisional_id}
                    onChange={e => set("provisional_id", e.target.value)}
                    placeholder="BL.XXX.YYY.NNNN or OB-XXXX"
                    className="bg-gray-900 border-gray-700 text-sm h-8"
                    data-testid="vouch-matter-ref"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-gray-400 mb-1 block">Client Type</label>
                  <Select value={form.client_type} onValueChange={v => set("client_type", v)}>
                    <SelectTrigger className="bg-gray-900 border-gray-700 text-sm h-8" data-testid="vouch-client-type">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-gray-700">
                      <SelectItem value="individual">Private Individual</SelectItem>
                      <SelectItem value="business">Business / Organisation</SelectItem>
                      <SelectItem value="trust">Trust</SelectItem>
                      <SelectItem value="llp">LLP / Partnership</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Row 3: Approval Date (BACKDATE) + Relationship */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-gray-400 mb-1 block flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Approval Date * <span className="text-status-amber/70">(can be backdated)</span>
                  </label>
                  <Input
                    type="date"
                    value={form.approval_date}
                    onChange={e => set("approval_date", e.target.value)}
                    className="bg-gray-900 border-gray-700 text-sm h-8"
                    data-testid="vouch-approval-date"
                    required
                  />
                </div>
                <div>
                  <label className="text-[11px] text-gray-400 mb-1 block">Relationship to Client</label>
                  <Input
                    value={form.relationship_to_client}
                    onChange={e => set("relationship_to_client", e.target.value)}
                    placeholder="e.g. Known for 5+ years through prior matters"
                    className="bg-gray-900 border-gray-700 text-sm h-8"
                    data-testid="vouch-relationship"
                  />
                </div>
              </div>

              {/* ID Documents Seen */}
              <div>
                <label className="text-[11px] text-gray-400 mb-1 block">ID Documents Seen / Alternative Evidence</label>
                <Input
                  value={form.id_documents_seen}
                  onChange={e => set("id_documents_seen", e.target.value)}
                  placeholder="e.g. Passport (expired), utility bill, professional reference letter"
                  className="bg-gray-900 border-gray-700 text-sm h-8"
                  data-testid="vouch-docs-seen"
                />
              </div>

              {/* Reason */}
              <div>
                <label className="text-[11px] text-gray-400 mb-1 block">Reason for Vouching * <span className="text-gray-600">(min 10 chars)</span></label>
                <textarea
                  value={form.reason}
                  onChange={e => set("reason", e.target.value)}
                  placeholder="Explain why standard Thirdfort/AML ID verification was not possible and the basis for vouching..."
                  className="w-full bg-gray-900 border border-gray-700 rounded-md text-sm p-2 text-white placeholder:text-gray-600 min-h-[60px] resize-y"
                  data-testid="vouch-reason"
                  required
                />
              </div>

              {/* Notes */}
              <div>
                <label className="text-[11px] text-gray-400 mb-1 block">Additional Notes</label>
                <textarea
                  value={form.notes}
                  onChange={e => set("notes", e.target.value)}
                  placeholder="Any additional context, conditions, or follow-up actions..."
                  className="w-full bg-gray-900 border border-gray-700 rounded-md text-sm p-2 text-white placeholder:text-gray-600 min-h-[40px] resize-y"
                  data-testid="vouch-notes"
                />
              </div>

              {/* Director info (read-only) */}
              <div className="bg-gray-900/60 rounded p-2 border border-gray-700/40 flex items-center gap-3 text-xs text-gray-400">
                <Shield className="w-3.5 h-3.5 text-teal" />
                <span>Vouching as: <strong className="text-white">{directorName}</strong> ({directorEmail})</span>
                <Badge className="bg-teal-dim/40 text-teal text-[10px] border-teal/50 ml-auto">
                  {directorEmail === "vanessa@bonsai.law" ? "COLP" : "COFA"}
                </Badge>
              </div>

              <div className="flex gap-2 pt-1">
                <Button
                  type="submit"
                  size="sm"
                  disabled={submitting}
                  className="bg-teal hover:bg-teal text-white"
                  data-testid="vouch-submit-btn"
                >
                  {submitting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                  {submitting ? "Recording..." : "Record Vouch"}
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setShowForm(false)} className="text-gray-400">
                  Cancel
                </Button>
              </div>
            </form>
          )}

          {/* Filter + list */}
          <div className="flex items-center gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-32 bg-gray-800 border-gray-700 text-xs h-7" data-testid="vouch-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-700">
                <SelectItem value="all">All records</SelectItem>
                <SelectItem value="active">Active only</SelectItem>
                <SelectItem value="revoked">Revoked only</SelectItem>
              </SelectContent>
            </Select>
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-500" />}
          </div>

          {/* Records list */}
          {vouches.length === 0 ? (
            <p className="text-xs text-gray-500 py-2">No vouching records found.</p>
          ) : (
            <div className="space-y-2" data-testid="vouch-records-list">
              {vouches.map((v, i) => (
                <div
                  key={i}
                  className={`rounded-lg border p-3 text-xs ${
                    v.status === "active"
                      ? "bg-teal-dim/20 border-teal/40"
                      : "bg-status-red-dim/10 border-red-800/30 opacity-70"
                  }`}
                  data-testid={`vouch-record-${i}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-1 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-white text-sm">{v.client_name}</span>
                        <Badge className={`text-[9px] px-1.5 ${
                          v.status === "active"
                            ? "bg-teal-dim/50 text-teal border-teal/50"
                            : "bg-status-red-dim/50 text-status-red border-red-700/50"
                        }`}>
                          {v.status === "active" ? "Active" : "Revoked"}
                        </Badge>
                        {v.client_type && (
                          <span className="text-gray-500">{v.client_type}</span>
                        )}
                      </div>
                      {v.matter_reference && (
                        <div className="text-gray-400">
                          <FileText className="w-3 h-3 inline mr-1" />Matter: <span className="text-gray-300 font-mono">{v.matter_reference}</span>
                        </div>
                      )}
                      {v.provisional_id && !v.matter_reference && (
                        <div className="text-gray-400">Legacy Ref: <span className="text-gray-300 font-mono">{v.provisional_id}</span></div>
                      )}
                      <div className="text-gray-400">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        Approval: <span className="text-white">{v.approval_date}</span>
                        {v.approval_date !== v.created_at?.split("T")[0] && (
                          <span className="text-status-amber/70 ml-1">(backdated)</span>
                        )}
                      </div>
                      <div className="text-gray-400">
                        Reason: <span className="text-gray-200">{v.reason}</span>
                      </div>
                      {v.id_documents_seen && (
                        <div className="text-gray-400">Docs seen: <span className="text-gray-300">{v.id_documents_seen}</span></div>
                      )}
                      {v.relationship_to_client && (
                        <div className="text-gray-400">Relationship: <span className="text-gray-300">{v.relationship_to_client}</span></div>
                      )}
                      {v.notes && (
                        <div className="text-gray-400">Notes: <span className="text-gray-300">{v.notes}</span></div>
                      )}
                      <div className="text-gray-500 pt-1">
                        Vouched by: {v.director_name} ({v.director_email}) — Recorded: {v.created_at?.split("T")[0]}
                      </div>
                      {v.status === "revoked" && (
                        <div className="text-status-red/80 pt-0.5">
                          Revoked: {v.revoked_at?.split("T")[0]} — {v.revoke_reason}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="ml-3 shrink-0 flex flex-col gap-1.5">
                      {/* PDF Download */}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDownloadPDF(v.client_name)}
                        className="text-blue-400/70 hover:text-blue-400 h-7 text-[10px] px-2"
                        data-testid="download-pdf-btn"
                      >
                        <Download className="w-3 h-3 mr-1" /> PDF
                      </Button>

                      {/* Revoke action */}
                      {v.status === "active" && (
                      <div className="ml-3 shrink-0">
                        {revoking === v.client_name ? (
                          <div className="space-y-1">
                            <Input
                              value={revokeReason}
                              onChange={e => setRevokeReason(e.target.value)}
                              placeholder="Revocation reason..."
                              className="bg-gray-900 border-red-700/50 text-xs h-7 w-44"
                              data-testid="revoke-reason-input"
                            />
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                onClick={() => handleRevoke(v.client_name)}
                                className="bg-status-red hover:bg-status-red text-white h-6 text-[10px] px-2"
                                data-testid="confirm-revoke-btn"
                              >
                                Confirm
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setRevoking(null); setRevokeReason(""); }}
                                className="text-gray-400 h-6 text-[10px] px-2"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setRevoking(v.client_name)}
                            className="text-status-red/60 hover:text-status-red h-7 text-[10px] px-2"
                            data-testid="revoke-btn"
                          >
                            <Ban className="w-3 h-3 mr-1" /> Revoke
                          </Button>
                        )}
                      </div>
                    )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          </>}
        </CardContent>
      )}
    </Card>
  );
}
